import Card from 'react-bootstrap/Card';
import { useContext, useEffect, useRef, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { savePrompt } from '../../services/savedPrompts';
import TickIcon from '../../assets/icon-google-tick.svg?react';
import BookMarkIcon from '../../assets/icon-google-bookmark.svg?react';
import './style.scss';
import OverlayTooltip from '../common/UI/OverlayTooltip';
import useElysiaStore from '@/app/elysia-store';
import CheveronIcon from '@/assets/icon-cheveron.svg?react';
import AttachedFilesList from './AttachedFilesList';

const PROMPT_LINE_LIMIT = 7;

const ChatUserMessage = (props) => {
  const { profileInfo } = useContext(AuthContext);

  const [showBadge, setShowBadge] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowMore, setShouldShowMore] = useState(false);

  const messageRef = useRef<HTMLDivElement>(null);
  const badgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const name = useElysiaStore((state) => state.name);
  const isPrivateChat = useElysiaStore((state) => state.isPrivateChat);
  const attachedFiles = props?.attachedFiles ?? [];

  const message = props?.messageBody || '';

  useEffect(() => {
    const element = messageRef.current;

    if (!element || !message) {
      setShouldShowMore(false);
      return;
    }

    const styles = window.getComputedStyle(element);
    const lineHeight = parseFloat(styles.lineHeight);

    if (!lineHeight) {
      return;
    }

    const maxHeight = lineHeight * PROMPT_LINE_LIMIT;

    setShouldShowMore(element.scrollHeight > maxHeight + 1);
  }, [message]);

  useEffect(() => {
    return () => {
      if (badgeTimeoutRef.current) {
        clearTimeout(badgeTimeoutRef.current);
      }
    };
  }, []);

  const handleBadge = () => {
    if (badgeTimeoutRef.current) {
      clearTimeout(badgeTimeoutRef.current);
    }

    setShowBadge(true);

    badgeTimeoutRef.current = setTimeout(() => {
      setShowBadge(false);
    }, 2000);
  };

  const handleSavePrompt = async () => {
    try {
      const response = await savePrompt(profileInfo?.userId || '', message);

      if (response) {
        handleBadge();
      }
    } catch (err) {
      console.log('error saving prompt', err);
    }
  };

  const shouldShowSavePrompt = !isPrivateChat && name !== 'Image Generator';

  return (
    <div className="user-msg-body user-msg-card mb-4">
      {/* Attached file badges row */}
      <AttachedFilesList files={attachedFiles} maxVisible={2} />

      {/* Bookmark action + message bubble, bottom-aligned as a row */}
      <div className="user-message-row">
        {shouldShowSavePrompt && (
          <div className="user-message-footer-left">
            <div className="saved-prompt-action">
              <OverlayTooltip
                tooltipText="Save Prompt"
                buttonContent={<BookMarkIcon aria-label="save prompt" />}
                ariaLabel="save prompt"
                testId="saved-prompt-icon"
                tooltipClassName="tooltip-sidebar"
                buttonClassName="saved-prompt-bookmark-icon"
                onClick={handleSavePrompt}
                placement="bottom"
              />

              {showBadge && (
                <span className="history-badge-for-saved-prompt-button" aria-live="assertive">
                  <TickIcon />
                  <span>Prompt saved</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* User message */}
        <Card className="custom-card">
          <Card.Body className="custom-card-body">
            <div
              ref={messageRef}
              className={`user-message-body ${shouldShowMore && !isExpanded ? 'collapsed' : ''}`}
              data-testid="user-prompt-body"
            >
              {message}
            </div>

            {/* Show more / Show less */}
            {shouldShowMore && (
              <div className="user-message-footer">
                <button
                  type="button"
                  className="show-more-button d-flex"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? 'Show less' : 'Show more'}
                  <CheveronIcon className={isExpanded ? 'cheveron-expanded' : ''} />
                </button>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default ChatUserMessage;
